import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Tooltip } from "@material-ui/core";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Config de la App de Meta (rellenar en el .env del frontend):
//   REACT_APP_FACEBOOK_APP_ID       -> App ID publica
//   REACT_APP_FACEBOOK_CONFIG_ID    -> config_id del Embedded Signup (coexistencia)
//   REACT_APP_FACEBOOK_GRAPH_VERSION-> p.ej. v20.0
const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;
const FB_CONFIG_ID = process.env.REACT_APP_FACEBOOK_CONFIG_ID;
const FB_GRAPH_VERSION = process.env.REACT_APP_FACEBOOK_GRAPH_VERSION || "v20.0";

const FB_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com"];

// Carga el SDK de Facebook una sola vez (idempotente).
const loadFacebookSdk = () =>
  new Promise((resolve, reject) => {
    if (!FB_APP_ID) {
      reject(new Error("REACT_APP_FACEBOOK_APP_ID no configurado"));
      return;
    }
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FB_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: FB_GRAPH_VERSION,
      });
      resolve(window.FB);
    };
    const existing = document.getElementById("facebook-jssdk");
    if (existing) return; // fbAsyncInit lo resolvera
    const js = document.createElement("script");
    js.id = "facebook-jssdk";
    js.src = "https://connect.facebook.net/en_US/sdk.js";
    js.async = true;
    js.defer = true;
    js.crossOrigin = "anonymous";
    js.onerror = () => reject(new Error("No se pudo cargar el SDK de Facebook"));
    document.body.appendChild(js);
  });

const MetaEmbeddedSignup = ({ onSuccess, disabled }) => {
  const [loading, setLoading] = useState(false);
  // phone_number_id / waba_id llegan por el message event del popup ANTES que el code.
  const signupDataRef = useRef({ phone_number_id: null, waba_id: null });

  const configured = Boolean(FB_APP_ID && FB_CONFIG_ID);

  const handleSessionMessage = useCallback((event) => {
    if (!FB_ORIGINS.includes(event.origin)) return;
    try {
      const parsed =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      if (parsed?.type === "WA_EMBEDDED_SIGNUP") {
        // parsed.event: 'FINISH' | 'CANCEL' | 'FINISH_ONLY_WABA' ...
        if (parsed?.data?.phone_number_id) {
          signupDataRef.current.phone_number_id = parsed.data.phone_number_id;
        }
        if (parsed?.data?.waba_id) {
          signupDataRef.current.waba_id = parsed.data.waba_id;
        }
      }
    } catch (_e) {
      // mensajes no-JSON del SDK: ignorar
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleSessionMessage);
    return () => window.removeEventListener("message", handleSessionMessage);
  }, [handleSessionMessage]);

  // Precargar el SDK al montar: FB.login DEBE llamarse sincrono dentro del click del usuario
  // (si esperaramos a cargar el SDK tras el click, el navegador bloquearia el popup).
  useEffect(() => {
    if (configured) {
      loadFacebookSdk().catch(() => {
        /* se reintenta/avisa en el click */
      });
    }
  }, [configured]);

  const registerConnection = useCallback(
    async (code) => {
      // El evento message con los ids (phone_number_id/waba_id) puede llegar DESPUES que el
      // callback del code. Esperamos hasta ~2.5s antes de abortar (evita fallo por carrera).
      const deadline = Date.now() + 2500;
      while (
        (!signupDataRef.current.phone_number_id ||
          !signupDataRef.current.waba_id) &&
        Date.now() < deadline
      ) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 200));
      }
      const { phone_number_id, waba_id } = signupDataRef.current;
      if (!phone_number_id || !waba_id) {
        toast.error(i18n.t("embeddedSignup.errors.missingIds"));
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.post("/whatsapp/embedded-signup", {
          code,
          phone_number_id,
          waba_id,
        });
        toast.success(i18n.t("embeddedSignup.success"));
        if (onSuccess) onSuccess(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        signupDataRef.current = { phone_number_id: null, waba_id: null };
      }
    },
    [onSuccess]
  );

  const launch = useCallback(() => {
    if (!configured) {
      toast.error(i18n.t("embeddedSignup.errors.notConfigured"));
      return;
    }
    // El SDK se precargo en mount; si aun no esta listo, avisar (sin await -> no bloquea popup).
    if (!window.FB) {
      toast.error(i18n.t("embeddedSignup.errors.sdk"));
      loadFacebookSdk().catch(() => {});
      return;
    }
    setLoading(true);
    signupDataRef.current = { phone_number_id: null, waba_id: null };
    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (code) {
          registerConnection(code);
        } else {
          // usuario cerro el popup o rechazo permisos
          setLoading(false);
        }
      },
      {
        config_id: FB_CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          // flujo de Coexistence: onboard de usuarios de la app WhatsApp Business
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
        },
      }
    );
  }, [configured, registerConnection]);

  const button = (
    <Button
      variant="contained"
      color="primary"
      startIcon={
        loading ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />
      }
      onClick={launch}
      disabled={disabled || loading || !configured}
    >
      {i18n.t("embeddedSignup.button")}
    </Button>
  );

  if (!configured) {
    return (
      <Tooltip title={i18n.t("embeddedSignup.errors.notConfigured")}>
        <span>{button}</span>
      </Tooltip>
    );
  }
  return button;
};

export default MetaEmbeddedSignup;
